import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, label, hint, id, disabled, ...props },
  ref,
) {
  const inputId = id ?? props.name;
  return (
    <label
      className={cn(
        'group/ck flex min-h-10 cursor-pointer items-start gap-s3',
        disabled && 'cursor-not-allowed text-ds-muted',
        className,
      )}
    >
      <span className="relative mt-s1 inline-flex h-5 w-5 shrink-0">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <span
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded-[6px] border-[1.5px] border-ds-field bg-ds-card text-ds-on-accent',
            'peer-focus-visible:shadow-ring peer-disabled:border-ds-disabled-border peer-disabled:bg-ds-disabled',
            'group-has-[:checked]/ck:border-ds-teal group-has-[:checked]/ck:bg-ds-teal',
          )}
        >
          <Check
            className="hidden h-3.5 w-3.5 group-has-[:checked]/ck:block"
            strokeWidth={3}
            aria-hidden
          />
        </span>
      </span>
      {(label || hint) && (
        <span className="min-w-0">
          {label ? <span className={cn(TYPE.body, 'block')}>{label}</span> : null}
          {hint ? <span className={cn(TYPE.small, 'block text-ds-muted')}>{hint}</span> : null}
        </span>
      )}
    </label>
  );
});
